import { ProductAndServices } from "types/Models/productAndServices";
import { emptyApi } from "./emptyApi";
import { Invoice } from "types/Models/invoice";

export type RevenueSchedule = {
  period: string;
  period_start: string;
  period_end: string;
  booked_revenue: {
    booked_from_this_period_sum: number;
    booked_invoice_ids: string[];
  };
  recognized_revenue: {
    recognized_from_this_month: number;
    recognized_from_previous_months: number;
    recognized_as_of_period_end: number;
    total_recognized: number;
    recognized_invoice_ids_as_of_current_period: string[];
  };
  deferred_revenue: {
    deferred_from_this_month: number;
    deferred_from_previous_months: number;
    prev_deferred_and_recognized_in_current_month: number;
    deferred_revenue_as_of_period_end: number;
    total_deferred: number;
    deferred_invoice_ids_as_of_current_period: string[];
  };
  periodic_recognized_revenue: Array<{
    period: string;
    period_start: string;
    period_end: string;
    recognized_revenue: number;
    deferred_revenue: number;
    recognized_invoice_ids: string[];
    deferred_invoice_ids: string[];
  }>;
};

// Revenue Schedule Details Types
export type InvoiceDetailsResponse = {
  item_name: string;
  customer: {
    stripe_customer_id: null | string;
    uuid: string;
    name: string;
    email: string;
    phone: string;
    billing_address: {
      street_address: string;
      city: string;
      state: string;
      zipcode: string;
      country: string;
    };
  };
  product: ProductAndServices;
  amount: number;
  invoice_id: string;
  invoice_number: string;
  booked_amount: number;
  invoice_date: string;
  item_id: string;
  revenue_schedule: {
    period: string;
    period_start: string;
    period_end: string;
    amount: {
      source: string;
      parsedValue: number;
    };
    status: string;
  }[];
}[];

export type CustomerDetailsResponse = {
  customer: {
    stripe_customer_id: null;
    uuid: string;
    name: string;
    email: string;
    phone: string;
    billing_address: {
      street_address: string;
      city: string;
      state: string;
      zipcode: string;
      country: string;
    };
  };
  total_amount: {
    source: number;
    parsedValue: number;
  };
  invoice_count: number;
}[];

export type CustomerRevenueScheduleDetailsParams = {
  entityId: string;
  // invoice_ids: string;
  customer_ids?: string;
  product_ids?: string;
  recognition_type?: string;
  invoice_date_start?: string;
  invoice_date_end?: string;
  recognition_date_start?: string;
  recognition_date_end?: string;
};

export type InvoiceRevenueScheduleDetailsParams = {
  entityId: string;
  customer_ids?: string;
  product_ids?: string;
  recognition_type?: string;
  invoice_date_start?: string;
  invoice_date_end?: string;
  recognition_date_start?: string;
  recognition_date_end?: string;
};

export const billingApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getRevenueSchedule: build.query<
      {
        schedule: RevenueSchedule[];
        total_booked_revenue: number;
        total_recognized_revenue: number;
        total_deferred_revenue: number;
        total_deferred_revenue_as_of_end_date: number;
        document_id?: string;
      },
      {
        entityId: string;
        start_date: string;
        end_date: string;
        carry_forward_invoices: boolean;
        report_type?: string;
        generate_report?: boolean;
        export_to_mail?: boolean;
        include_deferrals?: boolean;
      }
    >({
      query: ({ entityId, ...params }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/revenue_schedule/`,
        params,
      }),
    }),

    getRevenueScheduleCustomerDetails: build.query<
      CustomerDetailsResponse,
      CustomerRevenueScheduleDetailsParams
    >({
      query: ({ entityId, ...params }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/revenue_schedule/details/`,
        params: { ...params, revenue_detail_view: "CUSTOMER" },
      }),
    }),

    getRevenueScheduleInvoiceDetails: build.query<
      InvoiceDetailsResponse,
      InvoiceRevenueScheduleDetailsParams
    >({
      query: ({ entityId, ...params }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/revenue_schedule/details/`,
        params: { ...params, revenue_detail_view: "INVOICE" },
      }),
    }),
  }),
});

export const {
  useGetRevenueScheduleQuery,
  useLazyGetRevenueScheduleQuery,
  useGetRevenueScheduleCustomerDetailsQuery,
  useGetRevenueScheduleInvoiceDetailsQuery,
} = billingApis;
